import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  List, SwipeAction, Dialog, NavBar, 
  Tag, Empty, Button, Toast, DotLoading,
  InfiniteScroll, PullToRefresh, Popup, Image,
  ImageViewer
} from 'antd-mobile';
import { PriceRecord } from '../types';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { tagColors } from '../utils'; // 导入标签颜色数组而不是随机函数

// 在文件顶部定义适当的类型
type TextAlignType = 'left' | 'right' | 'center' | 'justify' | 'start' | 'end';
type FlexWrapType = 'nowrap' | 'wrap' | 'wrap-reverse';
type WordBreakType = 'normal' | 'break-all' | 'break-word' | 'keep-all';

// 定义样式对象
const styles = {
  // 价格列表项样式
  priceListItem: {
    fontSize: '16px',
    padding: '10px 16px',
    borderRadius: '8px',
    marginBottom: '4px',
    backgroundColor: '#fff',
  },
  // 商品名称样式
  productTitle: {
    fontSize: '25px',
    fontWeight: 500,
    marginBottom: '6px',
    lineHeight: '1.2',
  },
  // 相似商品名称样式
  similarProductTitle: {
    fontSize: '20px', 
    color: '#666', 
    marginTop: '2px',
  },
  // 价格项
  priceItem: {
    position: 'relative',
    backgroundColor: 'white',
    borderRadius: '8px',
    margin: '10px',
    padding: '15px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  },
  // 详情弹窗样式
  detailPopup: {
    padding: '16px',
    paddingBottom: '32px',
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    flex: 1,
    overflowY: 'auto',
  },
  // 详情标签行
  detailItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #f5f5f5',
    width: '100%',
    alignItems: 'center',
  },
  // 标签名
  detailLabel: {
    fontSize: '18px',
    color: '#666',
    flex: '0 0 80px',
    textAlign: 'left' as TextAlignType,
  },
  // 标签值
  detailValue: {
    flex: 1,
    textAlign: 'right' as TextAlignType,
    fontSize: '18px',
    color: '#333',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word' as WordBreakType,
    maxWidth: '65%',
    paddingLeft: '8px',
  },
  // 价格突出显示
  priceValue: {
    color: '#f55',
    fontWeight: 'bold',
  },
  // 页面容器
  priceListPage: {
    maxWidth: '94%',
    margin: '10px auto',
    backgroundColor: '#f7f8fa',
  },
  // 价格记录信息块
  priceRecordInfo: {
    width: '100%',
  },
  // 列表项记录标签
  recordLabel: {
    fontSize: '18px',
    color: '#666',
    flex: '0 0 80px',
    textAlign: 'left' as TextAlignType,
  },
  // 列表项记录值
  recordValue: {
    flex: 1,
    textAlign: 'right' as TextAlignType,
    fontSize: '18px',
    color: '#333',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word' as WordBreakType,
  },
  // 列表项记录行
  recordItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 0',
    width: '100%',
    alignItems: 'center',
  },
  // 标签容器
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap' as FlexWrapType,
    marginTop: '8px',
    justifyContent: 'flex-end',
  },
  // 媒体列表
  mediaList: {
    marginTop: '10px',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '15px',
    paddingBottom: '10px',
  },
  // 媒体项
  mediaItem: {
    borderRadius: '4px',
    overflow: 'hidden',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
    position: 'relative',
  },
  // 列表样式
  list: {
    background: 'transparent',
    margin: 0, 
    padding: 0,
    '--border-inner': 'none',
    '--border-top': 'none',
    '--border-bottom': 'none',
  },
  // 列表项样式
  listItem: {
    backgroundColor: '#fff',
    marginBottom: '4px',
    borderRadius: '10px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
  },
  // 滑动操作
  swipeAction: {
    borderRadius: '10px',
    overflow: 'hidden',
    margin: 0,
    padding: 0,
  },
};

const PriceList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [priceRecords, setPriceRecords] = useState<PriceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<PriceRecord | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewType, setPreviewType] = useState<'image' | 'video'>('image');
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const pageSize = 10;
  const loadingRef = useRef(false);
  const initialLoadDone = useRef(false);
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // 判断是否是分享模式
  const isSharedMode = !!localStorage.getItem('current_share_id');

  // 根据标签内容确定颜色
  const getTagColor = useMemo(() => {
    // 颜色映射表，将utils.ts中的类名转换为实际颜色
    const colorMapping = {
      'bg-blue-100': '#dbeafe',
      'text-blue-800': '#1e40af',
      'bg-green-100': '#dcfce7',
      'text-green-800': '#166534',
      'bg-purple-100': '#f3e8ff',
      'text-purple-800': '#6b21a8',
      'bg-yellow-100': '#fef9c3',
      'text-yellow-800': '#854d0e',
      'bg-pink-100': '#fce7f3',
      'text-pink-800': '#9d174d'
    };
    
    const colorMap = new Map<string, {bg: string, text: string}>();
    
    return (tag: string) => {
      if (!colorMap.has(tag)) {
        // 使用标签字符串的哈希值来确定颜色索引，确保同一标签始终使用相同颜色
        const hashCode = tag.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        const colorIndex = hashCode % tagColors.length;
        const selectedColor = tagColors[colorIndex];
        
        // 转换为实际颜色
        colorMap.set(tag, {
          bg: colorMapping[selectedColor.bg as keyof typeof colorMapping] || '#f0f0f0',
          text: colorMapping[selectedColor.text as keyof typeof colorMapping] || '#333333'
        });
      }
      return colorMap.get(tag)!;
    };
  }, []);

  // 格式化匹配状态显示
  const getMatchStatusText = (isMatched?: number) => {
    switch (isMatched) {
      case 1:
        return '完全匹配';
      case 2:
        return '部分匹配';
      case 3:
        return '不匹配';
      default:
        return '未知';
    }
  };

  // 获取匹配状态的颜色
  const getMatchStatusColor = (isMatched?: number) => {
    switch (isMatched) {
      case 1:
        return '#52c41a'; // 绿色
      case 2:
        return '#faad14'; // 橙色
      case 3:
        return '#ff4d4f'; // 红色
      default:
        return '#999'; // 灰色
    }
  };

  // 加载数据
  const loadData = useCallback(async (isRefresh = false) => {
    if (loadingRef.current) {
      console.log('正在加载中，跳过重复请求');
      return;
    }
    
    loadingRef.current = true;
    setLoading(true);
    
    try {
      const page = isRefresh ? 1 : currentPage;
      const employeeId = user ? user.employeeId.toString()+"-"+user.realname : undefined;
      const currentShareId = localStorage.getItem('current_share_id');
      
      if (!employeeId) {
        console.error('未获取到用户ID，无法加载数据');
        Toast.show({
          icon: 'fail',
          content: '请先登录再查看数据',
        });
        setHasMore(false);
        return;
      }
      
      console.log(`正在加载第${page}页数据，每页${pageSize}条`);
      
      let response;
      if (currentShareId) {
        response = await api.getPriceList(page, pageSize, employeeId, currentShareId);
      } else {
        response = await api.getPriceList(page, pageSize, employeeId);
      }
      
      if (response.success) {
        // 检查数据结构，处理不同的返回格式
        let newRecords: PriceRecord[] = [];
        let paginationInfo = null;
        
        if (response.data && Array.isArray(response.data)) {
          // 标准数组格式
          newRecords = response.data;
        } else if (response.data && response.data.records && Array.isArray(response.data.records)) {
          // 包含records字段的格式
          newRecords = response.data.records;
          paginationInfo = {
            total: response.data.total,
            hasMore: response.data.total > (page * pageSize)
          };
        }
        
        // 新的记录处理
        if (isRefresh) {
          // 刷新重置数据
          setPriceRecords(newRecords);
          setCurrentPage(2); // 刷新后页码从2开始
        } else {
          // 追加新数据，避免重复
          setPriceRecords(prev => {
            const existingIds = new Set(prev.map(item => item.id));
            const uniqueNewRecords = newRecords.filter(item => !existingIds.has(item.id));
            return [...prev, ...uniqueNewRecords];
          });
          setCurrentPage(page + 1);
        }
        
        // 判断是否还有更多数据
        let moreDataAvailable = false;
        
        if (response.pagination) {
          moreDataAvailable = response.pagination.hasMore;
        } else if (paginationInfo) {
          moreDataAvailable = paginationInfo.hasMore;
        } else {
          // 没有明确的分页信息，仅当返回了完整的一页数据时才可能有更多
          moreDataAvailable = newRecords.length === pageSize;
        }
        
        // 如果没有返回任何记录，则一定没有更多数据
        if (newRecords.length === 0) {
          moreDataAvailable = false;
        }
        
        console.log(`加载结果: 获取到${newRecords.length}条记录，是否还有更多: ${moreDataAvailable}`);
        setHasMore(moreDataAvailable);

        // 查询总记录数和分页数据，传入过滤条件
        if (response.data && response.data.total !== undefined) {
          setTotalRecords(response.data.total);
        } else if (paginationInfo && paginationInfo.total !== undefined) {
          setTotalRecords(paginationInfo.total);
        } else if (response.pagination && response.pagination.total !== undefined) {
          setTotalRecords(response.pagination.total);
        }
      } else {
        console.error('加载数据失败:', response.error);
        Toast.show({
          icon: 'fail',
          content: `加载数据失败: ${response.error || '服务器错误'}`,
        });
        setHasMore(false);
      }
    } catch (error) {
      console.error('加载数据出错:', error);
      Toast.show({
        icon: 'fail',
        content: '加载数据失败',
      });
      setHasMore(false);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [currentPage, user, pageSize]);

  // 初始加载数据 - 只在组件首次挂载和user变化时执行
  useEffect(() => {
    if (user && !initialLoadDone.current) {
      console.log('执行初始数据加载');
      initialLoadDone.current = true;
      loadData(true);
    }
    
    return () => {
      loadingRef.current = false;
      initialLoadDone.current = false;
      setHasMore(true);
      setCurrentPage(1);
    };
  }, [user]);

  // 加载更多数据
  const loadMore = async () => {
    if (!hasMore || loadingRef.current || currentPage === 1 || !initialLoadDone.current) {
      console.log('跳过加载更多:', {
        hasMore,
        isLoading: loadingRef.current,
        currentPage,
        initialLoadDone: initialLoadDone.current
      });
      return;
    }
    
    console.log('触发加载更多...');
    await loadData(false);
  };

  // 下拉刷新处理
  const onRefresh = async () => {
    if (loadingRef.current) {
      console.log('正在加载中，跳过刷新');
      return;
    }
    console.log('触发下拉刷新...');
    await loadData(true);
  };

  // 删除记录
  const handleDelete = async (index: number) => {
    Dialog.confirm({
      content: '确定要删除这条记录吗？',
      onConfirm: async () => {
        try {
          const record = priceRecords[index];
          if (!record.id) {
            Toast.show({
              icon: 'fail',
              content: '记录ID无效',
            });
            return;
          }
          
          loadingRef.current = true;
          setLoading(true);
          const response = await api.deletePrice(record.id);
          
          if (response.success) {
            // 更新UI
            const newRecords = [...priceRecords];
            newRecords.splice(index, 1);
            setPriceRecords(newRecords);
            
            Toast.show({
              icon: 'success',
              content: '删除成功',
            });
          } else {
            Toast.show({
              icon: 'fail',
              content: `删除失败: ${response.error || '未知错误'}`,
            });
          }
        } catch (error) {
          Toast.show({
            icon: 'fail',
            content: '删除失败',
          });
        } finally {
          setLoading(false);
          loadingRef.current = false;
        }
      },
    });
  };

  // 编辑记录
  const handleEdit = (index: number) => {
    const record = priceRecords[index];
    if (!record.id) {
      Toast.show({
        icon: 'fail',
        content: '记录ID无效',
      });
      return;
    }
    
    // 存储记录ID到localStorage
    localStorage.setItem('editingId', record.id);
    navigate('/');
  };

  // 查看详情
  const viewDetail = (record: PriceRecord) => {
    setCurrentRecord(record);
    setDetailVisible(true);
  };

  // 查看媒体文件大图
  const handlePreviewMedia = (url: string, type: 'image' | 'video') => {
    setPreviewType(type);
    
    if (type === 'video') {
      // 视频使用原有预览
      setPreviewUrl(url);
      setPreviewVisible(true);
    } else {
      // 图片使用ImageViewer
      // 如果有当前记录，提取所有图片URL
      if (currentRecord && currentRecord.mediaUrls && currentRecord.mediaUrls.length > 0) {
        const imageUrls = currentRecord.mediaUrls.filter(
          mediaUrl => !mediaUrl.includes('.mp4') && !mediaUrl.includes('video')
        );
        const currentIndex = imageUrls.findIndex(imgUrl => imgUrl === url);
        if (currentIndex !== -1) {
          setCurrentImageIndex(currentIndex);
          setImageViewerVisible(true);
        }
      }
    }
  };

  // 渲染详情弹窗
  const renderDetailPopup = () => {
    if (!currentRecord) return null;

    return (
      <Popup
        visible={detailVisible}
        onMaskClick={() => setDetailVisible(false)}
        bodyStyle={{
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          height: '60vh',
          padding: '0',
          width: '100%',
          boxSizing: 'border-box',
          maxWidth: '94%',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          left: '3%',
          transform: 'translateX(-50%)'
        }}
        getContainer={document.body}
      >
        <div style={{
          padding: '16px',
          paddingBottom: '12px',
          margin: 0,
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h3 style={styles.productTitle}>
              {currentRecord.productName}
            </h3>
            {currentRecord.similarProductName && (
              <div style={styles.similarProductTitle}>
                【{currentRecord.similarProductName}】
              </div>
            )}
          </div>
          <Button 
            size="mini" 
            onClick={() => setDetailVisible(false)}
            style={{
              fontSize: '20px'
            }}
          >
            关闭
          </Button>
        </div>
        
        <div style={{ 
          overflowY: 'auto', 
          flex: 1,
          WebkitOverflowScrolling: 'touch',
          padding: '0 16px 20px 16px'
        }}>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>品类:</span>
            <span style={styles.detailValue}>{currentRecord.category}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>重量/ml:</span>
            <span style={styles.detailValue}>{currentRecord.weight ? `${Math.floor(currentRecord.weight)} 克` : '无'}</span>
          </div>
          
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>标价:</span>
            <span style={styles.detailValue}>{currentRecord.listPrice ? `¥${currentRecord.listPrice}` : '无'}</span>
          </div>
          
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>到手价:</span>
            <span style={{...styles.detailValue, ...styles.priceValue}}>¥{currentRecord.finalPrice}</span>
          </div>
          
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>地点:</span>
            <span style={styles.detailValue}>{currentRecord.location}</span>
          </div>
          
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>日期:</span>
            <span style={styles.detailValue}>{currentRecord.date}</span>
          </div>
          
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>创建:</span>
            <span style={styles.detailValue}>{formatTime(currentRecord.createTime)}</span>
          </div>
          
          {!isSharedMode && (
            <>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>绩效:</span>
                <span style={styles.detailValue}>{currentRecord.scoreLevel || '无'}</span>
              </div>
              
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>系数:</span>
                <span style={styles.detailValue}>{currentRecord.scoreRatio || '无'}</span>
              </div>
            </>
          )}
          
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>来源:</span>
            <span style={styles.detailValue}>{currentRecord.source || '无'}</span>
          </div>
          
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>匹配状态:</span>
            <span style={{
              ...styles.detailValue,
              color: getMatchStatusColor(currentRecord.isMatched),
              fontWeight: 'bold'
            }}>
              {getMatchStatusText(currentRecord.isMatched)}
            </span>
          </div>
          
          {currentRecord.tags && currentRecord.tags.length > 0 && (
            <div style={styles.detailItem}>
              <span style={styles.detailLabel}>标签:</span>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'flex-end'
              }}>
                {currentRecord.tags.map((tag, index) => {
                  const colorStyle = getTagColor(tag);
                  return (
                    <Tag 
                      key={index} 
                      style={{ 
                        marginRight: '4px',
                        backgroundColor: colorStyle.bg,
                        color: colorStyle.text
                      }}
                    >
                      {tag}
                    </Tag>
                  );
                })}
              </div>
            </div>
          )}
          
          {currentRecord.mediaUrls && currentRecord.mediaUrls.length > 0 && (
            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
              <div style={styles.detailLabel}>照片:</div>
              <div style={{
                marginTop: '10px',
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '15px',
                paddingBottom: '10px'
              }}>
                {currentRecord.mediaUrls.map((url, index) => {
                  const isVideo = url.includes('.mp4') || url.includes('video');
                  return (
                    <div 
                      key={index} 
                      style={{
                        borderRadius: '4px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        cursor: 'pointer'
                      }}
                      onClick={() => handlePreviewMedia(url, isVideo ? 'video' : 'image')}
                    >
                      {isVideo ? (
                        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                          <video src={url} muted style={{ width: '100%' }} />
                          <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '40px',
                            height: '40px',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            borderRadius: '50%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            color: 'white',
                            fontSize: '18px'
                          }}>▶</div>
                        </div>
                      ) : (
                        <Image src={url} fit="cover" width="100%" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* 底部填充，确保所有内容可滚动到视图中 */}
          <div style={{ height: '20px' }}></div>
        </div>
      </Popup>
    );
  };

  // 媒体预览弹窗 - 仅视频
  const renderPreviewPopup = () => {
    return (
      <Popup
        visible={previewVisible}
        onMaskClick={() => setPreviewVisible(false)}
        bodyStyle={{
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          height: '100vh',
          width: '100vw',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'fixed',
          top: 0,
          left: 0,
          overflow: 'auto'
        }}
        getContainer={document.body}
      >
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative'
        }}>
          <video
            src={previewUrl}
            controls
            autoPlay
            style={{ maxWidth: '100%', maxHeight: '90vh' }}
            onClick={(e) => e.stopPropagation()}
          />
          <Button
            onClick={() => setPreviewVisible(false)}
            style={{
              position: 'absolute', 
              bottom: 30, 
              left: '50%', 
              transform: 'translateX(-50%)',
              top: 'auto',
              right: 'auto',
              borderRadius: '20px',
              fontSize: '16px',
              paddingLeft: '25px',
              paddingRight: '25px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              border: 'none'
            }}
          >
            关闭
          </Button>
        </div>
      </Popup>
    );
  };
  // 格式化时间为 "YYYY-MM-DD HH:MM:SS" 格式，并转换为北京时间
  const formatTime = (timeStr?: string) => {
    if (!timeStr) return '无';
    try {
      const date = new Date(timeStr);
      // 转换为北京时间 (UTC+8)
      //date.setHours(date.getHours() + 8);
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      return timeStr;
    }
  };

  return (
    <div style={styles.priceListPage}>
      <NavBar
        back='返回'
        onBack={() => navigate('/')}
        style={{ fontSize: '18px' }}
      >
        采集记录
      </NavBar>

      {!loading && (
        <div style={{ 
          textAlign: 'left', 
          padding: '5px 0 10px 10px', 
          color: '#666',
          fontSize: '14px',
          borderBottom: '2px solid #eee',
          marginBottom: '5px',
          backgroundColor: '#fff'
        }}>
          共采集 {totalRecords} 次
        </div>
      )}

      <PullToRefresh
        onRefresh={onRefresh}
        canReleaseText="释放立即刷新"
        completeText="刷新成功"
        completeDelay={500}
      >
        {loading && priceRecords.length === 0 ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            padding: '40px' 
          }}>
            <DotLoading color='primary' />
            <span style={{ marginLeft: 8 }}>加载中...</span>
          </div>
        ) : priceRecords.length === 0 ? (
          <div>
            <Empty description='暂无记录' />
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              margin: '16px' 
            }}>
              <Button 
                color='primary' 
                onClick={() => loadData(true)}
                loading={loading}
              >
                刷新数据
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <List mode="card" style={{ background: 'transparent', margin: 0, padding: 0 }}>
              {priceRecords.map((record, index) => (
                <SwipeAction
                  key={record.id || index}
                  rightActions={[
                    // {
                    //   key: 'edit',
                    //   text: '编辑',
                    //   color: 'primary',
                    //   onClick: () => handleEdit(index)
                    // },
                    {
                      key: 'delete',
                      text: '删除',
                      color: 'danger',
                      onClick: () => handleDelete(index)
                    }
                  ]}
                  style={{ 
                    margin: 0, 
                    padding: 0 
                  }}
                >
                  <List.Item
                    title={
                      <div>
                        <div style={styles.productTitle}>{record.productName}</div>
                        {record.similarProductName && (
                          <div style={styles.similarProductTitle}>
                            【{record.similarProductName}】
                          </div>
                        )}
                      </div>
                    }
                    arrow={false}
                    clickable
                    onClick={() => viewDetail(record)}
                    description={
                      <div style={styles.priceRecordInfo}>
                        <div style={styles.recordItem}>
                          <span style={styles.recordLabel}>品类:</span>
                          <span style={styles.recordValue}>{record.category}</span>
                        </div>
                        <div style={styles.recordItem}>
                          <span style={styles.recordLabel}>重量/ml:</span>
                          <span style={styles.recordValue}>{record.weight ? `${Math.floor(record.weight)} 克` : '无'}</span>
                        </div>
                        <div style={styles.recordItem}>
                          <span style={styles.recordLabel}>标价:</span>
                          <span style={styles.recordValue}>{record.listPrice ? `¥${record.listPrice}` : '无'}</span>
                        </div>
                        <div style={styles.recordItem}>
                          <span style={styles.recordLabel}>到手价:</span>
                          <span style={{...styles.recordValue, ...styles.priceValue}}>¥{record.finalPrice}</span>
                        </div>
                        <div style={styles.recordItem}>
                          <span style={styles.recordLabel}>地点:</span>
                          <span style={styles.recordValue}>{record.location}</span>
                        </div>
                        <div style={styles.recordItem}>
                          <span style={styles.recordLabel}>日期:</span>
                          <span style={styles.recordValue}>{record.date}</span>
                        </div>
                        <div style={styles.recordItem}>
                          <span style={styles.recordLabel}>创建:</span>
                          <span style={styles.recordValue}>{formatTime(record.createTime)}</span>
                        </div>
                        {!isSharedMode && (
                          <>
                            <div style={styles.recordItem}>
                              <span style={styles.recordLabel}>绩效:</span>
                              <span style={styles.recordValue}>{record.scoreLevel || '无'}</span>
                            </div>
                            <div style={styles.recordItem}>
                              <span style={styles.recordLabel}>系数:</span>
                              <span style={styles.recordValue}>{record.scoreRatio || '无'}</span>
                            </div>
                          </>
                        )}
                        <div style={styles.recordItem}>
                          <span style={styles.recordLabel}>来源:</span>
                          <span style={styles.recordValue}>{record.source || '无'}</span>
                        </div>
                        <div style={styles.recordItem}>
                          <span style={styles.recordLabel}>匹配状态:</span>
                          <span style={{
                            ...styles.recordValue,
                            color: getMatchStatusColor(record.isMatched),
                            fontWeight: 'bold'
                          }}>
                            {getMatchStatusText(record.isMatched)}
                          </span>
                        </div>
                        {Array.isArray(record.tags) && record.tags.length > 0 && (
                          <div style={styles.tagsContainer}>
                            {record.tags.map((tag, tagIndex) => {
                              const colorStyle = getTagColor(tag);
                              return (
                                <Tag 
                                  key={tagIndex} 
                                  style={{ 
                                    marginRight: '4px', 
                                    fontSize: '14px',
                                    backgroundColor: colorStyle.bg,
                                    color: colorStyle.text
                                  }}
                                >
                                  {tag}
                                </Tag>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    }
                    style={styles.priceListItem}
                  />
                </SwipeAction>
              ))}
            </List>
            
            {/* 只有在需要加载更多且当前不在加载状态时显示InfiniteScroll */}
            {hasMore && !loading && initialLoadDone.current && (
              <InfiniteScroll 
                loadMore={loadMore} 
                hasMore={hasMore}
                threshold={250}
              />
            )}

            {/* 加载指示器 */}
            {loading && priceRecords.length > 0 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                padding: '12px' 
              }}>
                <DotLoading color='primary' />
                <span style={{ marginLeft: 8 }}>加载中...</span>
              </div>
            )}
            
            {/* 没有更多数据提示 */}
            {!hasMore && priceRecords.length > 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '16px', 
                color: '#999',
                fontSize: '14px'
              }}>
                —— 已经到底了 ——
              </div>
            )}
          </div>
        )}
      </PullToRefresh>

      {renderDetailPopup()}
      {renderPreviewPopup()}
      
      {/* 图片查看器 */}
      {currentRecord && currentRecord.mediaUrls && (
        <ImageViewer.Multi
          images={currentRecord.mediaUrls.filter(
            url => !url.includes('.mp4') && !url.includes('video')
          )}
          visible={imageViewerVisible}
          defaultIndex={currentImageIndex}
          onClose={() => setImageViewerVisible(false)}
          renderFooter={() => (
            <div
              style={{
                color: '#fff',
                textAlign: 'center',
                padding: '12px 0',
                fontSize: '14px'
              }}
            >
              支持双指放大缩小和滑动查看
            </div>
          )}
          getContainer={document.body}
        />
      )}
    </div>
  );
};

export default PriceList; 